import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, FileText, Download, FileSpreadsheet, Calendar,
  PieChart, TrendingUp, Euro, ChevronDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../context/LanguageContext';
import { exportFiscalData, exportFiscalPdf } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { toast } from 'sonner';

export default function ExportPage() {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const availableYears = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i
  );

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await exportFiscalData(selectedYear, 'json');
      setData(res.data);
    } catch (error) {
      console.error('Failed to load fiscal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const res = await exportFiscalData(selectedYear, 'csv');
      
      // Create and download CSV file
      const blob = new Blob([res.data.content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t('csv_downloaded'));
    } catch (error) {
      toast.error(t('export_error'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await exportFiscalPdf(selectedYear);
      
      // Create and download HTML file (user can print to PDF)
      const blob = new Blob([res.data.html_content], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = res.data.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success(t('report_downloaded'));
    } catch (error) {
      toast.error(t('export_error'));
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const categories = summary?.by_category ? Object.entries(summary.by_category).sort((a, b) => b[1].total - a[1].total) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-bold text-lg">
                  {t('tax_export')}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {t('annual_expense_report')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Year Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-primary" />
                  <span className="font-medium">
                    {t('tax_year')}
                  </span>
                </div>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Summary Card */}
        {summary && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-gradient-to-br from-primary to-orange-600 text-white">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-white/70 text-sm">
                      {t('total_expenses')}
                    </p>
                    <p className="text-3xl font-bold">{formatCurrency(summary.total_expenses)}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">
                      {t('transactions')}
                    </p>
                    <p className="text-3xl font-bold">{summary.transaction_count}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">
                      {t('monthly_average')}
                    </p>
                    <p className="text-3xl font-bold">{formatCurrency(summary.average_monthly)}</p>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">
                      {t('categories')}
                    </p>
                    <p className="text-3xl font-bold">{categories.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Export Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 gap-4"
        >
          <Button 
            size="lg" 
            className="h-16"
            onClick={handleExportCSV}
            disabled={exporting || !data?.transactions?.length}
          >
            <FileSpreadsheet className="w-5 h-5 mr-2" />
            <div className="text-left">
              <p className="font-bold">CSV / Excel</p>
              <p className="text-xs opacity-80">
                {t('for_accountant')}
              </p>
            </div>
          </Button>
          
          <Button 
            size="lg" 
            variant="outline"
            className="h-16"
            onClick={handleExportPDF}
            disabled={exporting || !data?.transactions?.length}
          >
            <FileText className="w-5 h-5 mr-2" />
            <div className="text-left">
              <p className="font-bold">PDF</p>
              <p className="text-xs opacity-80">
                {t('visual_report')}
              </p>
            </div>
          </Button>
        </motion.div>

        {/* Categories Breakdown */}
        {categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  {t('breakdown_by_category')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {categories.map(([cat, catData], idx) => (
                  <div key={cat}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{catData.category_name_fr}</span>
                      <span className="text-muted-foreground">
                        {formatCurrency(catData.total)} ({catData.percentage}%)
                      </span>
                    </div>
                    <Progress 
                      value={catData.percentage} 
                      className="h-2"
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Monthly Breakdown */}
        {summary?.by_month && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  {t('monthly_evolution')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
                  {Object.entries(summary.by_month).map(([month, monthData]) => {
                    const monthNames = language === 'fr'
                      ? ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
                      : ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
                    const monthIdx = parseInt(month) - 1;
                    const maxMonth = Math.max(...Object.values(summary.by_month).map(m => m.total));
                    const heightPct = maxMonth > 0 ? (monthData.total / maxMonth) * 100 : 0;
                    
                    return (
                      <div key={month} className="text-center">
                        <div className="h-20 flex items-end justify-center mb-1">
                          <div 
                            className="w-full max-w-8 bg-primary/20 rounded-t transition-all hover:bg-primary/40"
                            style={{ height: `${Math.max(heightPct, 5)}%` }}
                            title={`${formatCurrency(monthData.total)}`}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">{monthNames[monthIdx]}</p>
                        <p className="text-xs font-medium">{monthData.count > 0 ? formatCurrency(monthData.total) : '-'}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* No Data State */}
        {(!data?.transactions || data.transactions.length === 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="text-center py-12">
              <CardContent>
                <Euro className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">
                  {t('no_data_for_year')}{selectedYear}
                </h3>
                <p className="text-muted-foreground">
                  {t('add_transactions_for_report')}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <p className="text-sm text-blue-700">
                💡 {t('export_disclaimer')}
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
}
